import React, {useEffect, useMemo, useState} from "react";
import {FormattedMessage, injectIntl} from "react-intl";
import Row from "@amzn/meridian/row";
import Heading from "@amzn/meridian/heading";
import Column from "@amzn/meridian/column";
import Text from "@amzn/meridian/text";
import Select, {SelectOption} from "@amzn/meridian/select";
import {fetchConfigs, fetchFeatures} from "../../NetworkUtils";
import Button from "@amzn/meridian/button";
import axios from "axios";
import {getHeadersForNFMS, getNFMSEndpoint} from "../../Utils";
import {CONFIG_TYPES} from "../../Constants";
import {useHistory} from "react-router-dom";

const EditConfiguration = () => {
    const history = useHistory();
    const [featureId, setFeatureId] = useState();
    const [featureSearchQuery, setFeatureSearchQuery] = useState();
    const [configId, setConfigId] = useState();
    const [configSearchQuery, setConfigSearchQuery] = useState();
    const [features, setFeatures] = useState([]);
    const [configs, setConfigs] = useState([]);
    const [configTypeMap, setConfigTypeMap] = useState([]);

    useEffect(() => {
        fetchFeatures().then(response => setFeatures(response));
    }, []);

    useEffect(() => {
        if (featureId) {
            fetchConfigs(featureId).then(response => {
                setConfigs(response.map(config => config.name));
                setConfigTypeMap(response);
            });
        }
    }, [featureId]);

    const searchRegExpFeature = useMemo(
        () => new RegExp(featureSearchQuery, "i"),
        [featureSearchQuery]
    )

    const searchRegExpConfigs = useMemo(
        () => new RegExp(configSearchQuery, "i"),
        [configSearchQuery]
    )

    const matchedFeature = features.filter(
        feature => !featureSearchQuery || searchRegExpFeature.test(feature)
    )

    const matchedConfig = configs.filter(
        config => !configSearchQuery || searchRegExpConfigs.test(config)
    )

    const editConfig = () => {
        const configType = configTypeMap.find(config => config.name === configId).type;

        const params = { featureId: featureId, configId: configId };
        (async ()=>{
            if (configType === CONFIG_TYPES.G2S2) {
                const headers = await getHeadersForNFMS("GetG2S2Configuration");
                axios
                    .post(getNFMSEndpoint(), params, headers)
                    .then((response) => {
                        if (response.status === 200) {
                            history.push({
                                pathname: "/g2s2config",
                                state: {
                                    featureId: featureId,
                                    configId: configId,
                                    mcmId: response.data.g2s2Configuration.mcmId,
                                    template: response.data.g2s2Configuration.template,
                                    tableName: response.data.g2s2Configuration.tableName,
                                    labelName: response.data.g2s2Configuration.labelName,
                                    fields: response.data.g2s2Configuration.fields,
                                }
                            });
                        }
                    })
                    .catch(() => {
                    })
            } else if (configType === CONFIG_TYPES.SDC) {
                const headers = await getHeadersForNFMS("GetSdcConfiguration");
                axios
                    .post(getNFMSEndpoint(), params, headers)
                    .then((response) => {
                        if (response.status === 200) {
                            history.push({
                                pathname: "/sdcconfig",
                                state: {
                                    featureId: featureId,
                                    configId: configId,
                                    mcmId: response.data.sdcConfiguration.mcmId,
                                    template: response.data.sdcConfiguration.template,
                                    configName: response.data.sdcConfiguration.configName,
                                    ripName: response.data.sdcConfiguration.ripServiceName,
                                    schemaVersion: response.data.sdcConfiguration.schemaVersion,
                                    fields: response.data.sdcConfiguration.fields
                                }
                            });
                        }
                    })
                    .catch(() => {
                    })
            } else if (configType === CONFIG_TYPES.AWS_APP_CONFIG) {
                const headers = getHeadersForNFMS("GetAWSAppConfiguration");
                axios
                    .post(getNFMSEndpoint(), params, headers)
                    .then((response) => {
                        if (response.status === 200) {
                            history.push({
                                pathname: "/awsappconfig",
                                state: {
                                    featureId: featureId,
                                    configId: configId,
                                    mcmId: response.data.awsAppConfiguration.mcmId,
                                    template: response.data.awsAppConfiguration.template,
                                    accountID: response.data.awsAppConfiguration.accountID,
                                    applicationName: response.data.awsAppConfiguration.applicationName,
                                    environmentName: response.data.awsAppConfiguration.environmentName,
                                    configurationProfile: response.data.awsAppConfiguration.configurationProfile,
                                    fields: response.data.awsAppConfiguration.fields
                                }
                            });
                        }
                    })
                    .catch(() => {
                    })
            }
        })();
    }

    return (
        <div className="App">
            <Row widths={["grid-12"]} alignmentVertical="top" spacingInset="none none medium large">
                <Heading level={2} alignment="left">
                    <FormattedMessage id="editConfig.heading" defaultMessage="Edit Configuration"/>
                </Heading>
            </Row>
            <Row widths={["grid-3", "grid-2"]} alignmentVertical="top" spacingInset="medium none medium large">
                <Column spacing="200" alignmentHorizontal="left">
                    <Text type="b200" color="secondary">
                        <FormattedMessage id="editConfig.feature" defaultMessage="Feature"/>
                    </Text>
                    <Select searchQuery={featureSearchQuery} onSearch={setFeatureSearchQuery} value={featureId}
                            onChange={setFeatureId} placeholder="Select Feature" width={400}>
                        {matchedFeature.map(feature => (
                            <SelectOption label={feature} value={feature} key={feature} />
                        ))}
                    </Select>
                </Column>
            </Row>
            <Row widths={["grid-3", "grid-2"]} alignmentVertical="top" spacingInset="medium none medium large">
                <Column spacing="200" alignmentHorizontal="left">
                    <Text type="b200" color="secondary">
                        <FormattedMessage id="editConfig.configuration" defaultMessage="Configuration"/>
                    </Text>
                    <Select searchQuery={configSearchQuery} onSearch={setConfigSearchQuery} value={configId}
                            onChange={setConfigId} placeholder="Select Configuration" width={400}>
                        {matchedConfig.map(config => (
                            <SelectOption label={config} value={config} key={config} />
                        ))}
                    </Select>
                </Column>
            </Row>
            <Row widths={["grid-3", "grid-1"]} alignmentVertical="top" spacingInset="large none medium large">
                <Column spacing="200" alignmentHorizontal="left">
                    <Button onClick={editConfig} disabled={configId ? false : true}>
                        <FormattedMessage id="editConfig.remove" defaultMessage="Edit Config"/>
                    </Button>
                </Column>
            </Row>
        </div>
    );
}

export default injectIntl(EditConfiguration);


// WEBPACK FOOTER //
// ./src/components/edit-config/EditConfig.js